import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useLanguage } from '@/composables/shared/useLanguage';
import { useToastStore } from '@/stores/toast';

export default function useHotkey() {
  const toastStore = useToastStore();
  const { t } = useI18n();

  const { changeLanguage } = useLanguage();

  // 마지막으로 눌린 단축키 (kr, us, cn)
  const lastHotkey = ref<string | null>(null);

  // Ctrl 키가 눌려 있는지 여부
  const isCtrlDown = ref(false);

  // 사용자가 입력 중인 문자열 버퍼
  let keyboardInput = '';

  // 유효한 단축키 목록
  const validCodes = new Set(['KeyK', 'KeyJ', 'KeyE', 'KeyZ']);

  const keyToLanguageMapper = {
    KeyK: 'ko',
    KeyJ: 'ja',
    KeyE: 'en_US',
    KeyZ: 'zh',
  } as const;

  // 키가 눌릴 때마다 실행되는 핸들러
  const handleKeyDown = (e: KeyboardEvent) => {
    // Alt 키가 눌렸을 경우
    if (e.key === 'Alt') {
      isCtrlDown.value = true;
      keyboardInput = '';
      return;
    }

    const el = document.activeElement as HTMLElement | null;
    if (
      el &&
      (el.tagName === 'INPUT' ||
        el.tagName === 'TEXTAREA' ||
        el.isContentEditable)
    ) {
      return;
    }

    // Alt가 눌리지 않았거나 반복 입력일 경우 무시
    if (!e.altKey || !isCtrlDown.value || e.repeat) return;

    // ✅ IME/키보드 언어 무관: 물리 키(e.code) 기준으로 처리
    const key = e.code;
    if (!key || !validCodes.has(key)) return;

    //  윈도우 기본 단축키 선점 방지: 즉시 막기
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function')
      e.stopImmediatePropagation();

    keyboardInput = key;

    const lang =
      keyToLanguageMapper[keyboardInput as keyof typeof keyToLanguageMapper];
    changeLanguage(lang);
    toastStore.info(t('language_changed_to', { language: lang.toUpperCase() }));

    lastHotkey.value = keyboardInput;
    keyboardInput = '';
  };

  // 키에서 손을 뗄 때 실행되는 핸들러
  const handleKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'Alt') {
      isCtrlDown.value = false;
      keyboardInput = '';
    }
  };

  // 컴포넌트가 마운트될 때 이벤트 리스너 등록
  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    window.addEventListener('keyup', handleKeyUp, { capture: true });
  });

  // 컴포넌트가 언마운트될 때 이벤트 리스너 해제
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyDown, {
      capture: true,
    } as any);
    window.removeEventListener('keyup', handleKeyUp, { capture: true } as any);
  });

  // 외부에서 반응형 값 접근 가능하도록 반환
  return {
    lastHotkey,
    isCtrlDown,
  };
}
